import React from "react";

import pdf from "@react-pdf/renderer";
import { format } from "date-fns";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { parseDate } from "@/utils/datetime";
import { FragmentType } from "@/utils/type";

import DayIndicator from "./components/DayIndicator";
import Divider from "./components/Divider";
import Footer from "./components/Footer";
import Flex from "./components/layout/Flex";
import Subheader from "./components/Subheader";
import {
  mulish,
  mulishBold,
  mulishExtraBold,
  mulishExtraLight,
  mulishLight,
  mulishMedium,
  mulishSemiBold,
} from "./fonts";

const { Page, StyleSheet, Font, View, Text } = pdf;

Font.register({
  family: "Mulish",
  fonts: [
    {
      src: mulish,
      fontWeight: "normal",
    },
    {
      src: mulishBold,
      fontWeight: "bold",
    },
    {
      src: mulishExtraBold,
      fontWeight: "ultrabold",
    },
    {
      src: mulishExtraLight,
      fontWeight: "ultralight",
    },
    {
      src: mulishLight,
      fontWeight: "light",
    },
    {
      src: mulishMedium,
      fontWeight: "medium",
    },
    {
      src: mulishSemiBold,
      fontWeight: "semibold",
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "0px",
    fontFamily: "Mulish",
    color: colors.dark,
    fontSize: "16px",
  },
  container: {
    padding: "12px 24px 0px 24px",
  },
  title: {
    fontWeight: "bold",
    fontSize: "14px",
  },
  rowItem: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    fontSize: "10px",
    padding: "6px 4px",
  },
  grayBackground: {
    backgroundColor: colors.light,
  },
  category: {
    fontWeight: "bold",
    fontSize: "12px",
    margin: "12px 0px",
  },
  empty: {
    height: 400,
    justifyContent: "center",
    alignItems: "center",
  },
});

type Props = {
  data: FragmentType<typeof pdfShoppingListTemplateFragment>;
};

function isEven(value: number): boolean {
  return value % 2 == 0;
}

const pdfShoppingListTemplateFragment = graphql(`
  fragment PDFShoppingListTemplate on WeeklyShoppingList {
    startOfWeek
    endOfWeek
    itemsByCategory {
      category
      items {
        amount
        food {
          id
          name
        }
        foodPortion {
          id
          amount
          unitWithModifier
        }
      }
    }
  }
`);

const PDFShoppingListTemplate: React.FC<Props> = ({ data }) => {
  function weekLabel() {
    const weekStartDate = parseDate(data.startOfWeek);
    const weekEndDate = parseDate(data.endOfWeek);

    // If week starts and ends on same day, show "Jun 23, 2022"
    if (weekStartDate.getTime() === weekEndDate.getTime()) {
      return format(weekStartDate, "PP");
    }

    // If week starts and ends on same year, show "Jun 21 - Jun 28, 2022"
    if (weekStartDate.getFullYear() === weekEndDate.getFullYear()) {
      const startFormatted = format(weekStartDate, "MMM d");
      const endFormatted = format(weekEndDate, "PP");
      return `${startFormatted} - ${endFormatted}`;
    }

    // Otherwise, show "Dec 31, 2021 - Jan 7, 2022"
    const startFormatted = format(weekStartDate, "PP");
    const endFormatted = format(weekEndDate, "PP");
    return `${startFormatted} - ${endFormatted}`;
  }

  return (
    <Page size="A4" style={styles.page}>
      <Subheader title="Shopping List" />
      <View style={styles.container}>
        <DayIndicator day={weekLabel()} />
        {data.itemsByCategory.length === 0 ? (
          <View style={styles.empty}>
            <Text>No data</Text>
          </View>
        ) : (
          data.itemsByCategory.map((categories) => (
            <View key={categories.category}>
              <Text style={styles.category}>{categories.category}</Text>
              <Divider size="large" />
              {categories.items.map((item, index) => {
                const grayBackground = isEven(index + 1)
                  ? styles.grayBackground
                  : styles.rowItem;

                return (
                  <View style={[styles.rowItem, grayBackground]} key={index}>
                    <View style={{ width: "100%" }}>
                      <Flex justifyContent="spaceBetween" flexDirection="row">
                        <Text>{item.food.name}</Text>
                        <Text style={{ alignSelf: "flex-end" }}>
                          {item.amount} {item.foodPortion.unitWithModifier}
                        </Text>
                      </Flex>
                    </View>
                  </View>
                );
              })}
            </View>
          ))
        )}
        <Footer />
      </View>
    </Page>
  );
};

export default PDFShoppingListTemplate;
